"use client"

import Logo from "images/FullMark.inline.svg"
import EmailL from "images/Icons/EmailL.inline.svg"
import LinkedinL from "images/Icons/LinkedInL.inline.svg"
import SmallArrowSVG from "images/Icons/SmallArrow.inline.svg"
import team1 from "images/footer/Team1.png"
import team2 from "images/footer/Team2.png"
import team3 from "images/footer/Team3.png"
import StaticImage from "library/StaticImage"
import { isBrowser } from "library/deviceDetection"
import UniversalLink from "library/link"
import { css, fmobile, fresponsive, ftablet, styled } from "library/styled"
import { useEffect, useState } from "react"
import colors from "styles/colors"
import customEases from "styles/eases"
import textStyles from "styles/text"
import links from "utils/links"

export default function Footer() {
	const [currentYear, setCurrentYear] = useState("2025")

	useEffect(() => {
		if (isBrowser) {
			const date = new Date()
			const year = date.getFullYear()
			setCurrentYear(year.toString())
		}
	}, [])

	const footerLinks = [
		{
			key: "brokers",
			href: links.brokers,
			label: "For Brokers",
		},
		{
			key: "blog",
			href: links.blog,
			label: "Blog",
		},
		{
			key: "careers",
			href: links.careers,
			label: "Careers",
		},
		{
			key: "about",
			href: links.about,
			label: "About",
		},
		{
			key: "terms",
			href: links.terms,
			label: "Terms of Service",
		},

		{
			key: "privacy",
			href: links.privacy,
			label: "Privacy Policy",
		},
	]

	return (
		<OuterWrapper>
			<Wrapper data-header-hide>
				<TopSection>
					<BrandingSection>
						<TeamImages>
							<CircleImage
								src={team1}
								alt={"team member"}
								width={48}
								height={48}
							/>
							<CircleImage
								src={team2}
								alt={"team member"}
								width={48}
								height={48}
							/>
							<CircleImage
								src={team3}
								alt={"team member"}
								width={48}
								height={48}
							/>
						</TeamImages>
						<Tagline>
							Healthcare that doesn't
							<GreenText> get in the way.</GreenText>
						</Tagline>
					</BrandingSection>

					<LinksSection>
						{footerLinks.map((link) => (
							<NavLink key={link.key} className={link.key} href={link.href}>
								{link.label}
								<SmallWrapper className="arrow-wrapper">
									<div className="small-arrow-main">
										<SmallArrow />
									</div>
									<div className="small-arrow-alt">
										<SmallArrow />
									</div>
								</SmallWrapper>
							</NavLink>
						))}
					</LinksSection>
				</TopSection>

				<BottomSection>
					<LogoSection href={links.home} aria-label="Arlo Home">
						<ArloLogo />
					</LogoSection>

					<SocialAndCopyright>
						<SocialIcons>
							<SocialIcon href={links.mailto} aria-label="mailto">
								<EmailL />
							</SocialIcon>
							<SocialIcon href={links.linkedin} aria-label="LinkedIn">
								<LinkedinL />
							</SocialIcon>
						</SocialIcons>
						<Copyright>
							ALL RIGHTS RESERVED.
							<br />© {currentYear} RAILWAY HEALTH INC DBA ARLO.
						</Copyright>
					</SocialAndCopyright>
				</BottomSection>
			</Wrapper>
		</OuterWrapper>
	)
}

const OuterWrapper = styled("div", {
	...fresponsive(css`
		width: 100%;
		background-color: ${colors.white};
		padding: 0;
		display: grid;
		place-items: center;
		will-change: transform;
	`),

	...fmobile(css`
		padding: 24px 0;
	`),
})

const Wrapper = styled("footer", {
	...fresponsive(css`
		display: grid;
		grid-template-rows: auto auto;
		gap: 0;
		background-color: ${colors.white};
		color: ${colors.green400};
		border: 1px solid ${colors.green300};
		max-width: 1376px;
		margin: 32px auto;
		will-change: transform;
	`),

	...ftablet(css`
		max-width: 960px;
		width: 960px;
	`),

	...fmobile(css`
		max-width: 327px;
		width: 327px;
		margin: 0;
	`),
})

const TopSection = styled("div", {
	...fresponsive(css`
		display: grid;
		grid-template-columns: 512px 860px;
		border-bottom: 1px solid ${colors.green300};
	`),
	...ftablet(css`
		display: grid;
		grid-template-columns: 641px 319px;
		border-bottom: 1px solid ${colors.green300};
	`),

	...fmobile(css`
		display: flex;
		flex-direction: column;
		width: 100%;
	`),
})

const BrandingSection = styled("div", {
	...fresponsive(css`
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		gap: 22px;
		padding: 86px 40px 32px 32px;
		border-right: 1px solid ${colors.green300};
	`),
	...ftablet(css`
		padding: 252px 165px 32px 32px;
	`),

	...fmobile(css`
		border-right: none;
		padding: 48px 20px;
	`),
})

const TeamImages = styled(
	"div",
	fresponsive(css`
		display: flex;
	`),
)

const CircleImage = styled(
	StaticImage,
	fresponsive(css`
		width: 48px;
		height: 48px;
		border-radius: 50%;

		&:nth-of-type(1) {
			transform: translateX(0);
			z-index: 2;
		}

		&:nth-of-type(2) {
			transform: translateX(-10px);
			z-index: 1;
		}

		&:nth-of-type(3) {
			transform: translateX(-20px);
			z-index: 0;
		}
	`),
)

const Tagline = styled("h2", {
	...fresponsive(css`
		color: ${colors.green400};
		${textStyles.h5};
	`),

	...fmobile(css`
		${textStyles.bodyXL};
	`),
})

const GreenText = styled(
	"span",
	fresponsive(css`
		position: relative;
		display: inline-block;
		margin-left: 8px;
		color: ${colors.green200};

		/* using this value because we'll be animating to this instead of using italic */
		transform: skewX(-10deg);
	`),
)

const NavLink = styled(UniversalLink, () => ({
	...fresponsive(css`
		color: ${colors.green400};
		${textStyles.custom.bodyS};
		display: flex;
		justify-content: space-between;
		width: 242px;

		.small-arrow-main {
			transform: translateY(0);
			opacity: 1;
			transition:
				transform 0.3s ${customEases.icon},
				opacity 0.3s ${customEases.icon};
		}

		.small-arrow-alt {
			position: absolute;
			top: 0;
			left: 0;
			transform: translateY(10px);
			opacity: 0;
			transition:
				transform 0.3s ${customEases.icon},
				opacity 0.3s ${customEases.icon};
		}

		&:hover {
			color: ${colors.green200};
		}

		&:hover .small-arrow-main {
			transform: translateY(-10px);
			opacity: 0;
		}

		&:hover .small-arrow-alt {
			transform: translateY(0);
			opacity: 1;
		}

		&::before {
			display: none;
		}

		&::after {
			display: none;
		}
	`),
	...ftablet(css`
		width: 100%;
	`),

	...fmobile(css`
		width: 100%;
	`),
}))

const LinksSection = styled("div", {
	...fresponsive(css`
		display: flex;
		flex-flow: column wrap;
		padding: 202px 35px 32px;
		height: 294px;

		.brokers,
		.careers,
		.terms {
			border-bottom: 1px solid ${colors.green100};
			padding-bottom: 12px;
			margin-bottom: 12px;
		}
	`),

	...ftablet(css`
		flex-direction: column;
		justify-content: flex-end;
		height: auto;

		a {
			flex: unset;
		}

		.brokers,
		.careers,
		.blog,
		.terms {
			border-bottom: 1px solid ${colors.green100};
			padding-bottom: 12px;
			margin-bottom: 12px;
		}

		.about {
			margin-bottom: 64px;
		}

		padding: 188px 32px 32px;
		gap: 0;
	`),

	...fmobile(css`
		flex-direction: column;
		justify-content: flex-end;
		height: auto;

		a {
			flex: unset;
		}

		.brokers,
		.careers,
		.blog,
		.terms {
			border-bottom: 1px solid ${colors.green100};
			padding-bottom: 12px;
			margin-bottom: 12px;
		}

		.about {
			margin-bottom: 64px;
		}

		padding: 48px 20px;
		gap: 0;
	`),
})

const BottomSection = styled("div", {
	...fresponsive(css`
		position: relative;
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 20px;
		padding: 68px 32px 32px;
	`),
	...ftablet(css`
		padding: 159px 32px 32px;
	`),

	...fmobile(css`
		padding: 170px 20px 20px;
		display: flex;
		flex-direction: column;
	`),
})

const LogoSection = styled(
	UniversalLink,
	fresponsive(css`
		display: flex;
		flex-direction: column;
		gap: 10px;
	`),
)

const ArloLogo = styled(Logo, {
	...fresponsive(css`
		width: 761px;
		color: ${colors.black200};
		transition:
			color 0.3s ${customEases.icon},
			transform 0.3s ${customEases.icon};

		&:hover {
			color: ${colors.green400};
			transform: scale(1.01);
		}
	`),

	...ftablet(css`
		width: 529px;
	`),

	...fmobile(css`
		width: 286.6px;
	`),
})

const SocialAndCopyright = styled(
	"div",
	fresponsive(css`
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		justify-content: space-between;
		height: 100%;
	`),
)

const SocialIcons = styled("div", {
	...fresponsive(css`
		display: flex;
		gap: 15px;
	`),

	...ftablet(css`
		transform: translateY(-90px);
	`),

	...fmobile(css`
		position: absolute;
		top: 20px;
		right: 20px;
	`),
})

const SocialIcon = styled(
	UniversalLink,
	fresponsive(css`
		width: 32px;
		height: 32px;
		border-radius: 50%;
		display: grid;
		place-items: center;
		transition: transform 0.3s ${customEases.icon};

		svg {
			path {
				transition: stroke 0.3s ${customEases.icon};
			}
		}

		&:hover {
			transform: scale(1.05);

			svg {
				path {
					stroke: ${colors.green200};
				}
			}
		}
	`),
)

const Copyright = styled(
	"div",
	fresponsive(css`
		${textStyles.titleS};
		color: ${colors.green400};
		text-align: right;
	`),
)

const SmallWrapper = styled("div", {
	...fresponsive(css`
		width: 8px;
		height: 8px;
		position: relative;
		overflow: hidden;
	`),
})

const SmallArrow = styled(SmallArrowSVG, {
	...fresponsive(css`
		display: block;
		position: relative;
		width: 8px;
		height: 8px;
	`),
})
